import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "ml-auto" }

import { columns } from '@/tables/columns/vat';
    import { Head } from '@inertiajs/vue3';
    import { ref, watch, computed, onMounted } from 'vue';
    import { Table, Row } from '@tanstack/vue-table';
    import { updateFirstColumnWidth } from '@/composables/useTableSizing';
    import { useI18n } from 'vue-i18n';
    import { usePage } from '@inertiajs/vue3';
    import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
    import { useUpdateDatatableRow } from '@/composables/useUpdateDatatableRow';
    import { useVatStore } from '@/stores/vat';
    import { storeToRefs } from 'pinia';
    import AppLayout from '@/layouts/AppLayout.vue';
    import axios from 'axios';
    import DataTable from '@/components/datatable/DataTable.vue';
    import DataTableToolbarActions from '@/components/datatable/DataTableToolbarActions.vue';
    import MonthPicker from '@/components/MonthPicker.vue';
    import PageCard from '@/components/PageCard.vue';
    import QueryKey from '@/enums/QueryKey';
    import TableTypeSelect from '@/components/datatable/DataTableTypeSelect.vue';
    import ToggleShowPreviousSubmissions from '@/components/vat/ToggleShowPreviousSubmissions.vue';
    import type { SharedData, BreadcrumbItem, VatSubmission } from '@/types';

    
export default /*@__PURE__*/_defineComponent({
  __name: 'Vat',
  setup(__props) {

    const page = usePage<SharedData>();
    const { t } = useI18n();
    const vatStore = useVatStore();
    const { date, selectedGrouping } = storeToRefs(vatStore);

    const { updateDataTableRow } = useUpdateDatatableRow();

    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'menu.vat',
        },
    ];

    const { isPending, data } = useQuery({
        queryKey: [QueryKey.VAT, selectedCompanyId, date, selectedGrouping],
        queryFn: getVatData,
    });

    async function getVatData() {
        const { data } = await axios.get(route('v2.vat.table'), {
            params: {
                date: date.value,
                grouping: selectedGrouping.value,
            },
        });
        return data.data;
    }

    const dataTableRef = ref<{ table: Table<VatSubmission> } | undefined>(undefined);

    watch(
        () => dataTableRef.value?.table.getRowModel().rows.map((r) => r.id),
        () => {
            if (!dataTableRef.value) return;
            updateFirstColumnWidth(dataTableRef.value.table, 'company_name');
        },
        { deep: true },
    );

    const typeOptions = [
        { value: 'my_clients', label: 'dashboards.grouping.my_clients' },
        { value: 'all', label: 'dashboards.grouping.all' },
        { value: 'outsourcing', label: 'dashboards.grouping.outsourcing' },
        { value: 'powerbi', label: 'dashboards.grouping.powerbi' },
        { value: 'salary', label: 'dashboards.grouping.salary' },
        { value: 'roger_ai', label: 'dashboards.grouping.roger_ai' },
    ];

    const queryClient = useQueryClient();

    const updateVatField = useMutation({
        mutationFn: async ({
            rowIndex,
            columnId,
            value,
        }: {
            rowIndex: number;
            columnId: keyof VatSubmission;
            value: any;
        }) => {
            const rowData = data.value?.[rowIndex];
            if (!rowData) return;

            const payload = {
                field: columnId,
                value: value,
            };

            await axios.post(route('v2.vat.submission.update-field', rowData.id), payload);
        },
        onMutate: async ({ rowIndex, columnId, value }) => {
            await queryClient.cancelQueries({
                queryKey: [
                    QueryKey.VAT,
                    selectedCompanyId,
                    date,
                    selectedGrouping,
                ],
            });

            setQueryData(rowIndex, columnId, value);
        },

        onError: (error) => {
            console.error('Failed to update VAT field:', error);
        },
    });

    const updateData = (row: Row<VatSubmission>, columnId: keyof VatSubmission, value: any) => {
        if (columnId === 'total_vat') {
            setQueryData(row.index, columnId, value);
            return;
        }

        const original = row.original[columnId];
        const originalVal = original?.valueOf?.() ?? null;
        const newVal = value ?? null;

        if (Object.is(originalVal, newVal)) return;

        updateVatField.mutate({ rowIndex: row.index, columnId, value });
    };

    const setQueryData = (rowIndex: number, columnId: keyof VatSubmission, value: any) => {
        updateDataTableRow(
            [QueryKey.VAT, selectedCompanyId, date, selectedGrouping],
            rowIndex,
            columnId,
            value,
        );
    };

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(Head), {
      title: _unref(t)('menu.vat')
    }, null, 8, ["title"]),
    _createVNode(AppLayout, {
      breadcrumbs: breadcrumbs,
      "header-class": "w-full"
    }, {
      "header-actions": _withCtx(() => [
        _createElementVNode("span", _hoisted_1, [
          _createVNode(MonthPicker, {
            modelValue: _unref(vatStore).date,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(vatStore).date) = $event))
          }, null, 8, ["modelValue"])
        ])
      ]),
      default: _withCtx(() => [
        _createVNode(PageCard, null, {
          default: _withCtx(() => [
            _createVNode(DataTable, {
              ref_key: "dataTableRef",
              ref: dataTableRef,
              "table-key": _unref(QueryKey).VAT,
              columns: _unref(columns),
              data: _unref(data),
              loading: _unref(isPending),
              meta: { updateData: updateData },
              "default-sorting": [
                    {
                        id: 'company_name',
                        desc: false,
                    },
                ],
              "pinned-columns": {
                    left: ['company_name', 'period'],
                },
              "default-column-visibility": {
                    company_group: false,
                }
            }, {
              title: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('menu.vat')) + " ", 1),
                _createVNode(TableTypeSelect, {
                  modelValue: _unref(vatStore).selectedGrouping,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(vatStore).selectedGrouping) = $event)),
                  options: typeOptions
                }, null, 8, ["modelValue"])
              ]),
              actions: _withCtx(() => [
                _createVNode(DataTableToolbarActions, { name: "vat" }, {
                  default: _withCtx(() => [
                    _createVNode(ToggleShowPreviousSubmissions)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["table-key", "columns", "data", "loading", "meta"])
          ]),
          _: 1
        })
      ]),
      _: 1
    })
  ], 64))
}
}

})